import { useState, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useMediaInsights, MediaInsight, DeviceInsight } from '@/hooks/useMediaInsights';
import { useDevices } from '@/hooks/useDevices';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
    PieChart, Pie, Cell, ResponsiveContainer,
    XAxis, YAxis, CartesianGrid, Tooltip,
    AreaChart, Area
} from 'recharts';
import {
    Play, Monitor, Clock, TrendingUp,
    RefreshCw, Calendar, BarChart3,
    Tv, Activity, Search, ChevronDown, ChevronUp,
    Timer, Zap, ArrowUpRight, ArrowDownRight, Minus,
    Video, Image as ImageIcon, FileText, Hash, Target
} from 'lucide-react';

// Gradientes para cards
const CARD_GRADIENTS = {
    green: 'from-emerald-500/10 via-emerald-500/5 to-transparent',
    blue: 'from-blue-500/10 via-blue-500/5 to-transparent',
    amber: 'from-amber-500/10 via-amber-500/5 to-transparent',
    cyan: 'from-cyan-500/10 via-cyan-500/5 to-transparent',
};

// Formatar duração de forma amigável
const formatDuration = (ms: number): string => {
    if (!ms || ms <= 0) return '0s';
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
};

// Formatar número compacto
const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
};

// Formatar data relativa
const formatRelativeDate = (dateStr: string): string => {
    if (!dateStr) return 'Nunca';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

// Formatar data completa
const formatFullDate = (dateStr: string): string => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Componente de KPI Card individual
const KPICard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    gradient,
    trend,
    trendValue
}: {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ElementType;
    gradient: keyof typeof CARD_GRADIENTS;
    trend?: 'up' | 'down' | 'neutral';
    trendValue?: string;
}) => {
    const TrendIcon = trend === 'up' ? ArrowUpRight : trend === 'down' ? ArrowDownRight : Minus;
    const trendColor = trend === 'up' ? 'text-emerald-500' : trend === 'down' ? 'text-red-500' : 'text-gray-400';

    return (
        <Card className={`relative overflow-hidden border-0 bg-gradient-to-br ${CARD_GRADIENTS[gradient]} backdrop-blur-sm`}>
            <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-6 -translate-y-6">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-white/5 to-transparent" />
            </div>
            <CardContent className="p-4 relative">
                <div className="flex items-start justify-between">
                    <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
                        <p className="text-2xl md:text-3xl font-bold tracking-tight">{value}</p>
                        {subtitle && (
                            <p className="text-xs text-muted-foreground">{subtitle}</p>
                        )}
                        {trend && trendValue && (
                            <div className={`flex items-center gap-1 text-xs ${trendColor}`}>
                                <TrendIcon className="h-3 w-3" />
                                <span>{trendValue}</span>
                            </div>
                        )}
                    </div>
                    <div className="p-2 rounded-xl bg-background/50 backdrop-blur-sm">
                        <Icon className="h-5 w-5 text-muted-foreground" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// Componente de linha da tabela de mídia
const MediaTableRow = ({
    media,
    index,
    period,
    isExpanded,
    onToggle
}: {
    media: MediaInsight;
    index: number;
    period: number;
    isExpanded: boolean;
    onToggle: () => void;
}) => {
    const frequencyPerDay = period > 0 ? (media.total_plays / period).toFixed(1) : '0';
    const isTopPerformer = index < 3;

    // Determinar ícone baseado no título (se contém video, image, etc)
    const MediaIcon = media.media_title?.toLowerCase().includes('video') ? Video
        : media.media_title?.toLowerCase().includes('image') ? ImageIcon
            : FileText;

    return (
        <>
            <tr
                className={`
                    group cursor-pointer transition-all duration-200
                    hover:bg-muted/50 
                    ${isExpanded ? 'bg-muted/30' : ''}
                    ${isTopPerformer ? 'border-l-2 border-l-emerald-500' : ''}
                `}
                onClick={onToggle}
            >
                {/* Ranking */}
                <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                        <div
                            className={`
                                h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold
                                ${index === 0 ? 'bg-amber-500/20 text-amber-500' :
                                    index === 1 ? 'bg-gray-400/20 text-gray-400' :
                                        index === 2 ? 'bg-orange-600/20 text-orange-600' :
                                            'bg-muted text-muted-foreground'}
                            `}
                        >
                            {index + 1}
                        </div>
                    </div>
                </td>

                {/* Nome da Mídia */}
                <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted/50">
                            <MediaIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                            <p className="font-medium text-sm truncate max-w-[200px]">
                                {media.media_title || 'Mídia sem título'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                ID: {media.media_id?.slice(0, 8)}...
                            </p>
                        </div>
                    </div>
                </td>

                {/* Total de Exibições */}
                <td className="py-3 px-4 text-center">
                    <div className="flex flex-col items-center">
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                            {formatNumber(media.total_plays)}
                        </span>
                        <span className="text-[10px] text-muted-foreground">plays</span>
                    </div>
                </td>

                {/* Tempo Total */}
                <td className="py-3 px-4 text-center">
                    <div className="flex flex-col items-center">
                        <span className="font-medium">{formatDuration(media.total_duration_ms)}</span>
                        <span className="text-[10px] text-muted-foreground">exibido</span>
                    </div>
                </td>

                {/* Frequência/Dia */}
                <td className="py-3 px-4 text-center">
                    <Badge variant="secondary" className="font-mono text-xs">
                        {frequencyPerDay}/dia
                    </Badge>
                </td>

                {/* Dispositivos (Alcance) */}
                <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                        <Monitor className="h-3 w-3 text-blue-500" />
                        <span className="font-medium">{media.unique_devices}</span>
                    </div>
                </td>

                {/* Última Exibição */}
                <td className="py-3 px-4 text-right">
                    <div className="flex flex-col items-end">
                        <span className="text-sm">{formatRelativeDate(media.last_played_at)}</span>
                        <span className="text-[10px] text-muted-foreground">
                            {formatFullDate(media.last_played_at)}
                        </span>
                    </div>
                </td>

                {/* Expandir */}
                <td className="py-3 px-2">
                    <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                </td>
            </tr>

            {/* Linha expandida - detalhes secundários */}
            {isExpanded && (
                <tr className="bg-muted/20">
                    <td colSpan={8} className="py-4 px-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="p-3 rounded-lg bg-background/50">
                                <p className="text-xs text-muted-foreground mb-1">Duração Média</p>
                                <p className="font-semibold">{formatDuration(media.avg_duration_ms)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-background/50">
                                <p className="text-xs text-muted-foreground mb-1">% do Total</p>
                                <p className="font-semibold">
                                    {/* Isso será calculado no componente pai */}
                                    —
                                </p>
                            </div>
                            <div className="p-3 rounded-lg bg-background/50">
                                <p className="text-xs text-muted-foreground mb-1">Status</p>
                                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                                    <Activity className="h-3 w-3 mr-1" />
                                    Ativo
                                </Badge>
                            </div>
                            <div className="p-3 rounded-lg bg-background/50">
                                <p className="text-xs text-muted-foreground mb-1">Alcance</p>
                                <p className="font-semibold">{media.unique_devices} dispositivo{media.unique_devices > 1 ? 's' : ''}</p>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

// Componente Principal
const InsightsSection = () => {
    const { user, profile } = useAuth();
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const impersonatedUserId = params.get('impersonate') || undefined;
    const isImpersonating = !!impersonatedUserId && !!user?.email && user.email.toLowerCase().includes('admin');
    const effectiveUserId = isImpersonating ? impersonatedUserId : (user?.id || '');

    const {
        mediaInsights,
        deviceInsights,
        timeline,
        summary,
        loading,
        error,
        period,
        setPeriod,
        refetch
    } = useMediaInsights(effectiveUserId);

    const { devices } = useDevices(effectiveUserId);

    // Estados locais
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [showDeviceDetails, setShowDeviceDetails] = useState(false);

    // Filtrar mídias por busca
    const filteredMedias = useMemo(() => {
        if (!searchTerm.trim()) return mediaInsights;
        const term = searchTerm.toLowerCase();
        return mediaInsights.filter(m =>
            m.media_title?.toLowerCase().includes(term) ||
            m.media_id?.toLowerCase().includes(term)
        );
    }, [mediaInsights, searchTerm]);

    // Calcular métricas avançadas
    const advancedMetrics = useMemo(() => {
        const totalPlays = mediaInsights.reduce((sum, m) => sum + (m.total_plays || 0), 0);
        const totalDuration = mediaInsights.reduce((sum, m) => sum + (m.total_duration_ms || 0), 0);
        const uniqueMedias = mediaInsights.length;
        const avgPlaysPerMedia = uniqueMedias > 0 ? Math.round(totalPlays / uniqueMedias) : 0;
        const avgFrequencyPerDay = period > 0 ? (totalPlays / period).toFixed(1) : '0';

        return {
            totalPlays,
            totalDuration,
            uniqueMedias,
            avgPlaysPerMedia,
            avgFrequencyPerDay,
            topMedia: mediaInsights[0] || null,
        };
    }, [mediaInsights, period]);

    // Cores para gráfico de pizza (10 cores + 1 para "Outras")
    const PIE_COLORS = [
        '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
        '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
        '#94a3b8'
    ];

    // Dados para gráfico de pizza (distribuição de plays - Top 10 + Outras)
    const pieData = useMemo(() => {
        const topItems = mediaInsights.slice(0, 10);
        const othersPlays = mediaInsights.slice(10).reduce((sum, m) => sum + (m.total_plays || 0), 0);

        const data = topItems.map((media, index) => ({
            name: media.media_title?.slice(0, 25) || `Mídia ${index + 1}`,
            value: media.total_plays,
            color: PIE_COLORS[index % PIE_COLORS.length]
        }));

        if (othersPlays > 0) {
            data.push({
                name: `Outras (${mediaInsights.length - 10} mídias)`,
                value: othersPlays,
                color: PIE_COLORS[10]
            });
        }

        return data;
    }, [mediaInsights]);

    // Dados para timeline
    const lineData = useMemo(() => {
        return timeline.map((point) => ({
            data: new Date(point.period_start).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'short'
            }),
            exibições: point.total_plays,
            mídias: point.unique_medias
        })).reverse();
    }, [timeline]);

    // Toggle expansão de linha
    const toggleRowExpansion = (mediaId: string) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(mediaId)) {
                next.delete(mediaId);
            } else {
                next.add(mediaId);
            }
            return next;
        });
    };

    // Dispositivos ativos
    const activeDevices = devices?.filter(d => d.status === 'active') || [];

    // Loading state
    if (loading) {
        return (
            <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-28 rounded-xl" />
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Skeleton className="h-72 rounded-xl" />
                    <Skeleton className="h-72 rounded-xl" />
                </div>
                <Skeleton className="h-96 rounded-xl" />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="text-center py-12">
                <p className="text-red-500 mb-4">{error}</p>
                <Button onClick={refetch} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Tentar novamente
                </Button>
            </div>
        );
    }

    // Empty state
    const hasData = mediaInsights.length > 0;

    if (!hasData) {
        return (
            <div className="space-y-6">
                {/* KPI Cards vazios */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard
                        title="Mídias Exibidas"
                        value="0"
                        subtitle="mídias únicas"
                        icon={Target}
                        gradient="green"
                    />
                    <KPICard
                        title="Total de Exibições"
                        value="0"
                        subtitle="plays registrados"
                        icon={Play}
                        gradient="blue"
                    />
                    <KPICard
                        title="Tempo de Exibição"
                        value="0h"
                        subtitle="tempo total"
                        icon={Clock}
                        gradient="amber"
                    />
                    <KPICard
                        title="Frequência Diária"
                        value="0"
                        subtitle="exibições/dia"
                        icon={Zap}
                        gradient="cyan"
                    />
                </div>

                {/* Empty state message */}
                <div className="text-center py-16 border-2 border-dashed border-muted rounded-xl bg-muted/10">
                    <BarChart3 className="h-16 w-16 mx-auto text-muted-foreground mb-4 opacity-40" />
                    <h3 className="text-lg font-semibold mb-2">Coletando dados de exibição...</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        Os insights por mídia aparecerão aqui conforme suas mídias forem exibidas nos dispositivos.
                        Execute o SQL de configuração e aguarde as primeiras exibições.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header com controles */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold">Métricas por Mídia Individual</h2>
                    <p className="text-sm text-muted-foreground">
                        Análise detalhada de performance de cada mídia
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    {/* Seletor de período */}
                    <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-1">
                        <Calendar className="h-4 w-4 text-muted-foreground ml-2" />
                        {[7, 30, 90].map((days) => (
                            <Button
                                key={days}
                                variant={period === days ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setPeriod(days)}
                                className="h-7 text-xs px-3"
                            >
                                {days}d
                            </Button>
                        ))}
                    </div>

                    <Button variant="outline" size="sm" onClick={refetch} className="h-8">
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        Atualizar
                    </Button>
                </div>
            </div>

            {/* KPI Cards principais */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard
                    title="Mídias Exibidas"
                    value={advancedMetrics.uniqueMedias}
                    subtitle="mídias únicas em rotação"
                    icon={Target}
                    gradient="green"
                />
                <KPICard
                    title="Total de Exibições"
                    value={formatNumber(advancedMetrics.totalPlays)}
                    subtitle={`últimos ${period} dias`}
                    icon={Play}
                    gradient="blue"
                />
                <KPICard
                    title="Tempo de Exibição"
                    value={formatDuration(advancedMetrics.totalDuration)}
                    subtitle="tempo total acumulado"
                    icon={Clock}
                    gradient="amber"
                />
                <KPICard
                    title="Frequência Diária"
                    value={advancedMetrics.avgFrequencyPerDay}
                    subtitle="exibições por dia"
                    icon={Zap}
                    gradient="cyan"
                />
            </div>

            {/* Gráficos - Grid 2 colunas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Top Mídias - Ranking com barras de progresso */}
                <Card className="border-0 bg-gradient-to-br from-background to-muted/20">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                                <TrendingUp className="h-4 w-4 text-emerald-500" />
                                Top Mídias mais Exibidas
                            </CardTitle>
                            <Badge variant="secondary" className="text-xs">Top 5</Badge>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {mediaInsights.slice(0, 5).map((media, index) => {
                            const maxPlays = mediaInsights[0]?.total_plays || 1;
                            const percentage = ((media.total_plays / maxPlays) * 100).toFixed(0);
                            const totalPercentage = advancedMetrics.totalPlays > 0
                                ? ((media.total_plays / advancedMetrics.totalPlays) * 100).toFixed(1)
                                : '0';

                            return (
                                <div key={media.media_id} className="space-y-1.5">
                                    <div className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span
                                                className={`
                                                    flex-shrink-0 h-5 w-5 rounded text-[10px] font-bold flex items-center justify-center
                                                    ${index === 0 ? 'bg-amber-500/20 text-amber-600' :
                                                        index === 1 ? 'bg-gray-400/20 text-gray-500' :
                                                            index === 2 ? 'bg-orange-500/20 text-orange-600' :
                                                                'bg-muted text-muted-foreground'}
                                                `}
                                            >
                                                {index + 1}
                                            </span>
                                            <span className="truncate font-medium">
                                                {media.media_title || 'Sem título'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className="text-xs text-muted-foreground">
                                                {totalPercentage}%
                                            </span>
                                            <span className="font-semibold text-emerald-600 dark:text-emerald-400 min-w-[50px] text-right">
                                                {formatNumber(media.total_plays)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ${index === 0 ? 'bg-emerald-500' :
                                                index === 1 ? 'bg-blue-500' :
                                                    index === 2 ? 'bg-amber-500' :
                                                        'bg-gray-400'
                                                }`}
                                            style={{ width: `${percentage}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}

                        {mediaInsights.length === 0 && (
                            <div className="text-center py-8 text-muted-foreground text-sm">
                                Nenhuma mídia exibida ainda
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Timeline de exibições */}
                <Card className="border-0 bg-gradient-to-br from-background to-muted/20">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-muted-foreground" />
                            Exibições ao Longo do Tempo
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={lineData}>
                                <defs>
                                    <linearGradient id="colorPlays" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis dataKey="data" fontSize={11} tickLine={false} />
                                <YAxis fontSize={11} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'hsl(var(--background))',
                                        border: '1px solid hsl(var(--border))',
                                        borderRadius: '8px'
                                    }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="exibições"
                                    stroke="#10b981"
                                    fillOpacity={1}
                                    fill="url(#colorPlays)"
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Gráfico de Pizza - Distribuição de Exibições */}
            {pieData.length > 0 && (
                <Card className="border-0 bg-gradient-to-br from-background to-muted/20">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                                <Target className="h-4 w-4 text-blue-500" />
                                Distribuição de Exibições
                            </CardTitle>
                            <Badge variant="secondary" className="text-xs">
                                {advancedMetrics.totalPlays.toLocaleString('pt-BR')} total
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-col lg:flex-row items-center gap-6">
                            {/* Gráfico de Pizza (Donut) */}
                            <div className="w-full lg:w-1/2">
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={55}
                                            outerRadius={90}
                                            paddingAngle={2}
                                            dataKey="value"
                                            stroke="hsl(var(--background))"
                                            strokeWidth={2}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={entry.color}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value: number) => [
                                                `${value.toLocaleString('pt-BR')} exibições (${advancedMetrics.totalPlays > 0 ? ((value / advancedMetrics.totalPlays) * 100).toFixed(1) : 0}%)`,
                                                'Total'
                                            ]}
                                            contentStyle={{
                                                backgroundColor: 'hsl(var(--background))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '8px',
                                                fontSize: '12px'
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Legenda Lateral com Scroll */}
                            <div className="w-full lg:w-1/2">
                                <div className="max-h-[220px] overflow-y-auto pr-2 space-y-1 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
                                    {pieData.map((item, index) => {
                                        const percentage = advancedMetrics.totalPlays > 0
                                            ? ((item.value / advancedMetrics.totalPlays) * 100).toFixed(1)
                                            : '0';
                                        return (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <div
                                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: item.color }}
                                                    />
                                                    <span className="text-sm truncate">{item.name}</span>
                                                </div>
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                    <span className="text-xs text-muted-foreground">
                                                        {percentage}%
                                                    </span>
                                                    <span className="text-sm font-medium min-w-[60px] text-right">
                                                        {item.value.toLocaleString('pt-BR')}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {pieData.length > 5 && (
                                    <p className="text-[10px] text-muted-foreground text-center mt-2">
                                        ↕ Role para ver mais mídias
                                    </p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Tabela detalhada de mídias */}
            <Card className="border-0 bg-gradient-to-br from-background to-muted/20">
                <CardHeader className="pb-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            Detalhamento por Mídia
                            <Badge variant="outline" className="ml-2">{filteredMedias.length} mídias</Badge>
                        </CardTitle>

                        {/* Busca */}
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar mídia..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 h-9"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b bg-muted/30">
                                    <th className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-16">
                                        <Hash className="h-3 w-3" />
                                    </th>
                                    <th className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        Mídia
                                    </th>
                                    <th className="py-3 px-4 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        <div className="flex items-center justify-center gap-1">
                                            <Play className="h-3 w-3" />
                                            Exibições
                                        </div>
                                    </th>
                                    <th className="py-3 px-4 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        <div className="flex items-center justify-center gap-1">
                                            <Timer className="h-3 w-3" />
                                            Tempo
                                        </div>
                                    </th>
                                    <th className="py-3 px-4 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        <div className="flex items-center justify-center gap-1">
                                            <Zap className="h-3 w-3" />
                                            Freq/Dia
                                        </div>
                                    </th>
                                    <th className="py-3 px-4 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        <div className="flex items-center justify-center gap-1">
                                            <Monitor className="h-3 w-3" />
                                            Alcance
                                        </div>
                                    </th>
                                    <th className="py-3 px-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        Última Exibição
                                    </th>
                                    <th className="py-3 px-2 w-10"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50">
                                {filteredMedias.map((media, index) => (
                                    <MediaTableRow
                                        key={media.media_id}
                                        media={media}
                                        index={index}
                                        period={period}
                                        isExpanded={expandedRows.has(media.media_id)}
                                        onToggle={() => toggleRowExpansion(media.media_id)}
                                    />
                                ))}
                            </tbody>
                        </table>

                        {filteredMedias.length === 0 && (
                            <div className="text-center py-12">
                                <Search className="h-8 w-8 mx-auto text-muted-foreground mb-2 opacity-50" />
                                <p className="text-sm text-muted-foreground">
                                    Nenhuma mídia encontrada para "{searchTerm}"
                                </p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Seção secundária - Dispositivos (colapsável) */}
            <Card className="border-0 bg-muted/20">
                <CardHeader
                    className="cursor-pointer hover:bg-muted/30 transition-colors rounded-t-xl"
                    onClick={() => setShowDeviceDetails(!showDeviceDetails)}
                >
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Monitor className="h-4 w-4 text-muted-foreground" />
                            Distribuição por Dispositivo
                            <Badge variant="outline" className="ml-2 text-xs">
                                {deviceInsights.length} dispositivos
                            </Badge>
                        </CardTitle>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                            {showDeviceDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                    </div>
                </CardHeader>

                {showDeviceDetails && deviceInsights.length > 0 && (
                    <CardContent className="pt-0 space-y-4">
                        {/* Gráfico de pizza - Status dos dispositivos (ideal para poucos itens) */}
                        {devices && devices.length > 0 && (
                            <div className="flex flex-col lg:flex-row gap-4 items-center">
                                <div className="w-full lg:w-1/3">
                                    <ResponsiveContainer width="100%" height={160}>
                                        <PieChart>
                                            <Pie
                                                data={(() => {
                                                    const active = devices.filter(d => d.status === 'active').length;
                                                    const inactive = devices.filter(d => d.status === 'inactive').length;
                                                    const offline = devices.filter(d => d.status !== 'active' && d.status !== 'inactive').length;
                                                    return [
                                                        { name: 'Ativos', value: active, fill: '#10b981' },
                                                        { name: 'Inativos', value: inactive, fill: '#f59e0b' },
                                                        { name: 'Offline', value: offline, fill: '#94a3b8' },
                                                    ].filter(item => item.value > 0);
                                                })()}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={40}
                                                outerRadius={65}
                                                paddingAngle={3}
                                                dataKey="value"
                                            >
                                            </Pie>
                                            <Tooltip
                                                formatter={(value: number) => [`${value} dispositivo${Number(value) > 1 ? 's' : ''}`, '']}
                                                contentStyle={{
                                                    backgroundColor: 'hsl(var(--background))',
                                                    border: '1px solid hsl(var(--border))',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    {/* Legenda */}
                                    <div className="flex justify-center gap-4 text-xs">
                                        <div className="flex items-center gap-1">
                                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                            <span>Ativos</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div className="w-2 h-2 rounded-full bg-amber-500" />
                                            <span>Inativos</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div className="w-2 h-2 rounded-full bg-slate-400" />
                                            <span>Offline</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Cards de dispositivos */}
                                <div className="flex-1 w-full">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {deviceInsights.slice(0, 4).map((device) => (
                                            <div
                                                key={device.device_id}
                                                className="flex items-center gap-3 p-3 bg-background/50 rounded-lg border border-border/50"
                                            >
                                                <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                                    <Tv className="h-5 w-5 text-blue-500" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm truncate">
                                                        {device.device_name || 'Dispositivo'}
                                                    </p>
                                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <Play className="h-3 w-3" />
                                                            {formatNumber(device.total_plays)} plays
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <FileText className="h-3 w-3" />
                                                            {device.unique_medias} mídias
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                )}
            </Card>
        </div>
    );
};

export default InsightsSection;
